import {
  Box,
  Button,
  Heading,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  VStack,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { Navbar } from "../components/landing/Navbar";
import { HeroSection } from "../components/landing/HeroSection";
import { StatsSection } from "../components/landing/StatsSection";
import { ServicesSection } from "../components/landing/ServicesSection";
import { ContactSection } from "../components/landing/ContactSection";
import { FundsSection } from "../components/landing/FundsSection";
import { BlogSection } from "../components/landing/BlogSection";
import { Footer } from "../components/landing/Footer";
import { messages } from "../services/api";
import { getMessageCta, getMessageImage } from "../utils/messages";

export const LandingPage = () => {
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const hasOpenedHomeMessage = useRef(false);
  const modalBg = useColorModeValue("white", "gray.900");
  const headingColor = useColorModeValue("gray.900", "white");
  const textColor = useColorModeValue("gray.700", "gray.200");
  const { data: homeMessageResponse } = useQuery({
    queryKey: ["messages", "home"],
    queryFn: messages.getHome,
  });
  const homeMessage = homeMessageResponse?.message;
  const homeMessageImage = homeMessage ? getMessageImage(homeMessage) : "";
  const homeMessageCta = getMessageCta(homeMessage);

  useEffect(() => {
    const scrollTo = (location.state as any)?.scrollTo;
    if (!scrollTo) return;
    // Give page time to fully render sections
    const timer = setTimeout(() => {
      const el = document.querySelector(scrollTo);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => clearTimeout(timer);
  }, [location.state]);

  useEffect(() => {
    if (homeMessage && !hasOpenedHomeMessage.current) {
      hasOpenedHomeMessage.current = true;
      onOpen();
    }
  }, [homeMessage, onOpen]);

  return (
    <Box dir="rtl" overflowX="hidden">
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(6px)" />
        <ModalContent bg={modalBg} borderRadius="2xl" mx={4} overflow="hidden">
          <ModalCloseButton left={4} right="auto" zIndex={1} color="#333" />
          <ModalBody p={0}>
            <VStack spacing={0} align="stretch">
              {homeMessageImage ? (
                <Image
                  src={homeMessageImage}
                  alt={homeMessage?.title || "پیام صفحه اصلی"}
                  w="full"
                  objectFit="cover"
                />
              ) : null}
              <VStack spacing={1} align="stretch" p={{ base: 5, md: 6 }}>
                <Heading size="md" color={headingColor} lineHeight="1.8">
                  {homeMessage?.title}
                </Heading>
                <Text color={textColor} lineHeight="2" whiteSpace="pre-wrap">
                  {homeMessage?.body}
                </Text>
                {homeMessageCta ? (
                  homeMessageCta.type === "external" ? (
                    <Button
                      as="a"
                      href={homeMessageCta.url}
                      target={
                        homeMessageCta.openInNewTab ? "_blank" : undefined
                      }
                      rel={
                        homeMessageCta.openInNewTab
                          ? "noopener noreferrer"
                          : undefined
                      }
                      onClick={onClose}
                      mt={4}
                      w="full"
                      colorScheme="brand"
                    >
                      {homeMessageCta.label}
                    </Button>
                  ) : (
                    <Button
                      as={RouterLink}
                      to={homeMessageCta.url}
                      onClick={onClose}
                      mt={4}
                      w="full"
                      colorScheme="brand"
                    >
                      {homeMessageCta.label}
                    </Button>
                  )
                ) : null}
              </VStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Navbar />
      <HeroSection />
      <StatsSection />
      <ServicesSection />
      <FundsSection />
      <BlogSection />
      <ContactSection />
      <Footer />
    </Box>
  );
};
